import classNames from "classnames";
import { useEffect, useLayoutEffect } from "react";
import AppLayout from "@/layouts/AppLayout";
import usePageTitle from "@/hooks/usePageTitle";
import { DOMAIN, PERMISSION, useDenyAuthorization, useRequireCapability } from "@/hooks/useCapabilities";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import McpPlaygroundChat from "@/features/mcp-playground/McpPlaygroundChat";
import { isStaffMcpPlaygroundRuntime } from "@/features/mcp-playground/access";
import { CHAT_LOCK_VIEWPORT_CLASS } from "@/lib/documentBodyOverflow";

const DEFAULT_VIEWPORT = "width=device-width, initial-scale=1.0";

export default function ChatPage() {
  usePageTitle("Chat with Member 360");
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  useRequireCapability({ domain: DOMAIN.FACILITIES, permission: PERMISSION.viewFacilities });
  const denyAuthorization = useDenyAuthorization();
  const { isCompactLayout } = useIsCompactLayout();

  useEffect(() => {
    if (!isStaffMcpPlaygroundRuntime()) {
      denyAuthorization();
    }
  }, [denyAuthorization]);

  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.add(CHAT_LOCK_VIEWPORT_CLASS);

    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const previousViewport = viewportMeta?.getAttribute("content") ?? null;
    if (viewportMeta) {
      const content = previousViewport || DEFAULT_VIEWPORT;
      if (!content.includes("viewport-fit=cover")) {
        viewportMeta.setAttribute("content", `${content}, viewport-fit=cover`);
      }
    }

    const syncLockHeight = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      const offsetTop = window.visualViewport?.offsetTop ?? 0;
      html.style.setProperty("--chat-lock-height", `${height}px`);
      html.style.setProperty("--chat-lock-offset", `${offsetTop}px`);
    };

    syncLockHeight();
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener("resize", syncLockHeight);
    visualViewport?.addEventListener("scroll", syncLockHeight);
    window.addEventListener("resize", syncLockHeight);

    return () => {
      visualViewport?.removeEventListener("resize", syncLockHeight);
      visualViewport?.removeEventListener("scroll", syncLockHeight);
      window.removeEventListener("resize", syncLockHeight);
      html.style.removeProperty("--chat-lock-height");
      html.style.removeProperty("--chat-lock-offset");
      html.classList.remove(CHAT_LOCK_VIEWPORT_CLASS);
      if (viewportMeta) {
        if (previousViewport === null) {
          viewportMeta.removeAttribute("content");
        } else {
          viewportMeta.setAttribute("content", previousViewport);
        }
      }
    };
  }, []);

  return (
    <AppLayout lockViewport>
      <div
        className={classNames("flex min-h-0 w-full flex-1 flex-col", {
          "mx-auto max-w-3xl pt-4 lg:pt-6": !isCompactLayout
        })}
        data-testid="mcp-playground-page-shell"
      >
        <McpPlaygroundChat />
      </div>
    </AppLayout>
  );
}
